import {
  ACTION_ARGS,
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  INPUT_FIELD_ARGS,
  INPUT_FORM_ARGS,
  INPUT_GROUP_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import Container from '../core/container.gjs';
import LabeledBase from './labeled/base.gjs';
import Super from '../core/base.gjs';
import styles from './radio-button.module.scss';

const METADATA = Metadata.define({
  description: `Selection controls for when a user can only select one item from a list, where all of the items are shown.

### Required arguments
The \`@id\` and \`@onClick\` arguments are always required.

#### \`@id\`
The \`@id\` argument is required to properly link the label to the radio button input.

#### \`@onClick\`
The \`@onClick\` action is required to get the state of the radio button on user interaction, as well as the \`event\` object from the click event.
It also provides identifying information about the radio button that was clicked, such as its \`@id\` and \`@value\` values.
These are received as an object with attributes named \`checked\`, \`event\`, \`id\`, and \`value\`.

### Named blocks
This component exposes a \`:label\` named block that can be used for more complex label content than simple text.
If you only need to include text content, then the \`@label\` argument is preferred.`,
  extends: [
    pick(ACTION_ARGS, ['onBlur', 'onChange', { name: 'onClick', required: true }, 'onFocus']),
    pick(COMPONENT_STATE_ARGS, ['disabled', { name: 'id', initialValue: 'styleguide-radio' }]),
    pick(BUTTON_ARGS, [{ name: 'label', initialValue: 'Example label' }]),
    pick(INPUT_FIELD_ARGS, [{ name: 'checked', initialValue: false }]),
    pick(INPUT_FORM_ARGS, ['name', 'required', { name: 'value', initialValue: 'example value' }]),
    pick(INPUT_GROUP_ARGS, [{ name: 'groupName', initialValue: 'radio-group' }]),
  ],
  name: 'Radio Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=24908-9851&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/613f82-radio-button',
  },
});

export default class SpdsInputRadioButton extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-radio-button';
  styles = styles;
  type = 'radio';

  get maskId() {
    return this.composeSubcomponentId('mask');
  }

  <template>
    <LabeledBase
      @additionalArguments={{this.arguments}}
      @component={{this.constructor.metadata.component}}
      @checked={{@checked}}
      @disabled={{@disabled}}
      @groupName={{@groupName}}
      @id={{this.id}}
      @name={{@name}}
      @onBlur={{@onBlur}}
      @onChange={{@onChange}}
      @onClick={{@onClick}}
      @onFocus={{@onFocus}}
      @required={{@required}}
      @type={{this.type}}
      @value={{@value}}
      class={{this.classes}}
      {{this.validateArguments}}
      ...attributes
    >
      <Container
        class="spds-input-mask spds-radio-button"
        data-test-spds-input-radio-button={{this.id}}
        id={{this.maskId}}
      />
      {{#if (has-block "label")}}
        {{yield to="label"}}
      {{else}}
        {{@label}}
      {{/if}}
    </LabeledBase>
  </template>
}

export const INPUT_RADIO_BUTTON_METADATA = SpdsInputRadioButton.metadata;
